/**
 * Utilities for computing initial baseline data for the Score History chart.
 *
 * Vanderbilt reassessments derive their baseline from the last chronological
 * entry of their corresponding initial assessment.
 *
 * PHQ-9 and GAD-7 use the first (earliest) chronological assessment as the
 * baseline, since there is no separate initial/reassessment split.
 */

import { ClinicalMeasurement, LongitudinalEntry } from "../types";

export interface BaselineResult {
  date: string;
  responder: string;
  /** Map of subscale name → score value used as the reference line. */
  scores: Record<string, number>;
}

/** Maps a Vanderbilt reassessment measure name to its initial assessment counterpart. */
const VANDERBILT_REASSESSMENT_TO_INITIAL: Record<string, string> = {
  "Vanderbilt: Parent Reassessment": "Vanderbilt: Parent Initial Assessment",
  "Vanderbilt: Teacher Reassessment": "Vanderbilt: Teacher Initial Assessment",
};

/** Subscale names tracked for Vanderbilt baselines. */
const VANDERBILT_BASELINE_SUBSCALES = new Set([
  "Predominantly Inattentive",
  "Predominantly Hyperactive/Impulsive",
  "Average Performance Score",
]);

/** Measure names whose first assessment serves as the baseline. */
const SCREENER_BASELINE_MEASURES = new Set([
  "PHQ-9 Assessment",
  "GAD-7 Assessment",
]);

function extractScores(
  entry: LongitudinalEntry,
  filter?: Set<string>,
): Record<string, number> {
  const scores: Record<string, number> = {};
  for (const subscale of entry.subscales) {
    if (!filter || filter.has(subscale.name)) {
      scores[subscale.name] = subscale.score;
    }
  }
  return scores;
}

/**
 * Computes the baseline reference data for the Score History chart.
 *
 * Returns `null` when:
 *  - The measure does not use baselines.
 *  - There is no applicable baseline data (e.g. missing initial assessment,
 *    or only one PHQ-9/GAD-7 entry exists — no prior point to compare to).
 */
export function computeInitialBaseline(
  measureName: string,
  measureDataMap: Record<string, ClinicalMeasurement>,
): BaselineResult | null {
  // --- Vanderbilt reassessments: baseline = last entry of the initial assessment ---
  const initialKey = VANDERBILT_REASSESSMENT_TO_INITIAL[measureName];
  if (initialKey) {
    const initialData = measureDataMap[initialKey];
    if (!initialData?.longitudinalData?.length) {
      return null;
    }

    const lastEntry =
      initialData.longitudinalData[initialData.longitudinalData.length - 1];

    return {
      date: lastEntry.date,
      responder: lastEntry.responder || "",
      scores: extractScores(lastEntry, VANDERBILT_BASELINE_SUBSCALES),
    };
  }

  // --- PHQ-9 / GAD-7: baseline = first (earliest) chronological assessment ---
  if (SCREENER_BASELINE_MEASURES.has(measureName)) {
    const currentData = measureDataMap[measureName];
    if (
      !currentData?.longitudinalData ||
      currentData.longitudinalData.length <= 1
    ) {
      // No baseline when there is only one or zero data points
      return null;
    }

    // longitudinalData is already sorted chronologically by the backend
    const firstEntry = currentData.longitudinalData[0];

    return {
      date: firstEntry.date,
      responder: firstEntry.responder || "",
      scores: extractScores(firstEntry),
    };
  }

  return null;
}
